// src/utils/faviconSafety.js
// Guards the page-group favicon against pointing at our own proxy.
//
// The requests table renders <img src={origin}/favicon.ico> per page group,
// with the origin taken from the row's page URL. When a group's URL is a
// proxy-served page (the /connect setup page, or any row captured against the
// proxy's own address), that img fetches https://<proxy-ip>:8000/favicon.ico.
// The proxy used to forward that to itself and amplify — ~500 rows/s, which
// filled the 3000-row history buffer and evicted a live capture session. The
// proxy now short-circuits self-targeted requests (proxy-server/selfTarget.js);
// this is the other half — never aim a favicon there to begin with.

import { resolveProxyWsUrl } from './proxyWs';

// The proxy's three listeners: MITM, REST API, WebSocket.
export const PROXY_PORTS = new Set(['8000', '8001', '9000']);

function hostOf(url) {
  try { return new URL(url).hostname.toLowerCase(); } catch { return ''; }
}

// Hostnames we know belong to the proxy, from the same config the app uses to
// reach it. Covers the prod hostnames (proxy.adlensfx.com / proxy-api…) where
// the port alone would not give it away.
function proxyHosts() {
  const hosts = new Set();
  const wsUrl = (() => { try { return resolveProxyWsUrl(); } catch { return ''; } })();
  for (const u of [wsUrl, process.env.REACT_APP_PROXY_API_URL]) {
    const h = hostOf(u);
    if (h) hosts.add(h);
  }
  return hosts;
}

/**
 * True when `origin` addresses the proxy itself rather than a real content
 * host. Errs toward true: a missing favicon is cosmetic, a request loop is not.
 * @param {string} origin — e.g. "https://104.131.164.175:8000"
 */
export function isProxyOrigin(origin) {
  let u;
  try { u = new URL(origin); } catch { return false; }
  if (u.port && PROXY_PORTS.has(u.port)) return true;
  return proxyHosts().has(u.hostname.toLowerCase());
}

/**
 * Favicon URL for a page group, or null when none should be requested.
 * @param {string} pageUrl — the group's page URL ("(unknown)" for no page).
 */
export function faviconUrlFor(pageUrl) {
  if (!pageUrl || pageUrl === '(unknown)') return null;
  let origin;
  try { origin = new URL(pageUrl).origin; } catch { return null; }
  if (isProxyOrigin(origin)) return null;
  return `${origin}/favicon.ico`;
}
